import { Card, CardBody, CardHeader, Col, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap'
import { getProperties, getMeters, getUtilities, getPropertyUtilities, getPropertyUtilityMeters } from '../../Services/ClientService';
import { getBills, getBillsData } from '../../Services/ClientService';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { Href } from '../../Utils/Constants';
import Table from '../../CustomComponent/Tables/Table';
import { TableColumn } from 'react-data-table-component';
import { MoreVertical, Eye, ChartColumnIncreasing, ChartLine } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup'; // Import yup
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { formatDate, numberFormat, sortByKey } from '../../Utils';
import SelectDropdown from '../../CustomComponent/Form/SelectDropdown';
import ColumnChart from '../../CustomComponent/Charts/ColumnChart';
import BasicAreaChart from '../../CustomComponent/Charts/BasicAreaChart';
import { Btn, H4 } from '../../AbstractElements';

export const justifyTabNav = [{ id: '1', nav: 'Bill Report' }, { id: '2', nav: 'Bill Charts' }];
type billParams = {
    site_id?: string,
    company_id: string,
    property_id?: string,
    utility_id?: string,
    year?: string,
    check_type?: string,
    type_mode?: string,
    with_previous_year?: boolean,
    bill_status?: string
}

type YearEntry = {
    year: string;
    label: string[];
    data: number[];
}

export interface BillTableRow {
    property?: { name?: string, [key: string]: any };
    bill_date: string;
    bill_type: string;
    start_date: string;
    end_date: string;
    meter?: any;
    utility?: { name?: string; si_unit?: string, [key: string]: any };
    supplier?: { name?: string };
    total_consumption: number | string;
    cost: number | string;
    currency?: { major_symbol?: string };
    status: string;
}

//const filterKeys = ['property', 'utility', 'meter', 'supplier', 'bill_date', 'bill_type', 'start_date', 'end_date', 'total_consumption', 'cost', 'currency', 'status'];
const filterKeys: (keyof BillTableRow)[] = ['property', 'utility', 'meter', 'supplier', 'bill_date', 'bill_type', 'start_date', 'end_date', 'total_consumption', 'cost', 'currency', 'status'];

const schema = yup.object().shape({
    company_id: yup.string().trim().required('Client is required'),
    comment: yup.string().trim().optional(),
    status: yup.string().trim().required(),
});

const filterSchema = yup.object().shape({
    property_id: yup.string().trim().optional(),
    year: yup.string().trim().optional(),
    utility_id: yup.string().trim().optional(),
});

export default function EnergyBills() {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedUtility, setSelectedUtility] = useState('');
    const [bills, setBills] = useState<BillTableRow[]>([]);
    const [meters, setMeters] = useState<any>([]);
    const [propertyMeters, setPropertyMeters] = useState<any>([]); // fetched from the db by utility
    const [propertyUtilities, setPropertyUtilities] = useState<any>([]);
    const [utilities, setUtilities] = useState<any>([]);
    const [utility, setUtility] = useState('');
    const [billData, setBillData] = useState<any>(null);
    const [selectedMeter, setSelectedMeter] = useState('');
    const [basicTab, setBasicTab] = useState('1');
    const [reloadData, setReloadData] = useState(false);
    const [activeChart, setActiveChart] = useState('bar');
    const [timeMode, setTimeMode] = useState('Year');
    const [typeMode, setTypeMode] = useState('consumption');
    const [selectedYear, setSelectedYear] = useState<number>();

    const { control, setValue, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema), // Use the schema for validation
    });

    const {
        control: filterControl,
        watch: filterWatch,
        formState: { errors: filterErrors },
        reset: resetFilter,
    } = useForm({
        resolver: yupResolver(filterSchema), // Use the schema for validation
        defaultValues: {
            property_id: '',
            utility_id: '',
            year: '',
        }
    });

    const client = watch('company_id');
    const propertyId = filterWatch("property_id");
    const utilityId = filterWatch("utility_id");
    const year = filterWatch("year");

    useEffect(() => {
        const storedClient = localStorage.getItem('selectedClient');
        if (storedClient) {
            setValue('company_id', storedClient)
        }

        fetchUtilities()
    }, []);

    useEffect(() => {
        if (!client) return;
        fetchBills({ company_id: client });
        fetchMeters()
        fetchProperties(client).then(data => setProperties(sortByKey(data, 'name')));
        localStorage.setItem('selectedClient', client)
    }, [client]);

    useEffect(() => {
        if (!client || !reloadData) return;
        fetchBills({ company_id: client });
        setReloadData(false)
    }, [client, reloadData]);

    useEffect(() => {
        if (!propertyId) return;
        handlePropertyChange(propertyId)
    }, [propertyId])

    useEffect(() => {
        if (!utilityId) return;
        handleUtilityChange(utilityId)
    }, [utilityId])

    const handlePropertyChange = (selectedValue: any) => {
        setSelectedProperty(selectedValue as string);
        fetchPropertyUtilities(selectedValue as string);
    };

    const handleUtilityChange = (selectedValue: any) => {
        setSelectedUtility(selectedValue as string);
        fetchUtilityMeters(selectedValue as string);
    };

    const fetchProperties = async (clientId: string) => {
        if (clientId == '') return
        const { properties } = await getProperties();
        return properties
    }

    const fetchBills = async ({ company_id }: billParams) => {
        if (company_id == '') return
        const billData = await getBills({ company_id });
        setBills(billData);
    }

    const fetchMeters = async () => {
        const meterData = await getMeters();
        setMeters(meterData);
    }

    const fetchUtilities = async () => {
        const data = await getUtilities();
        setUtilities(data);
    }

    const fetchPropertyUtilities = async (propertyId: string) => {
        try {
            const data: any[] = await getPropertyUtilities(propertyId);
            setPropertyUtilities(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch Properties');
        }
    };

    const fetchUtilityMeters = async (utilityId: string) => {
        try {
            const data: any[] = await getPropertyUtilityMeters(selectedProperty, utilityId);
            setPropertyMeters(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch Properties');
        }
    };

    const ActionDropdown = ({ row }: { row: any }) => {
        const [dropdownOpen, setDropdownOpen] = useState(false);
        const toggle = () => setDropdownOpen(prevState => !prevState);

        return (
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                <DropdownToggle color="transparent" className="p-0 border-0 shadow-none">
                    <MoreVertical size={18} />
                </DropdownToggle>
                <DropdownMenu end>
                    <DropdownItem onClick={() => navigate(`${process.env.PUBLIC_URL}/bills/${row.bill_id}`)}>
                        <Eye size={16} color="green" className="me-2" />
                        View
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        );
    };

    const billTableColumns: TableColumn<BillTableRow>[] = [
        {
            name: "Property",
            cell: (row: any) => {
                const status = row.status;

                const colorMap: Record<string, string> = {
                    'in_review': '#FFC107',   // Amber
                    'dont_pay': '#FF3737',   // Red
                    'pay': '#34C759',         // Green
                };

                const color = colorMap[status] || '#ccc';

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate(`${process.env.PUBLIC_URL}/bills/${row.bill_id}`)}>
                        <div
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: color,
                                flexShrink: 0,
                            }}
                        />
                        <span className="text-primary" style={{ whiteSpace: 'nowrap' }}>{row.property?.name || ''}</span>
                    </div>
                );
            },
            width: '200px',
            sortable: false,
            center: false,
        },
        {
            name: "Utility",
            cell: (row) => row.utility?.name || '',
            sortable: false,
            center: false,
        },
        {
            name: "Meter",
            cell: (row) => row.meter?.name || '',
            sortable: false,
            center: false,
            width: '170px',
        },
        {
            name: "Bill Date",
            selector: (row) => formatDate(row.bill_date),
            sortable: true,
            sortFunction: (a: any, b: any) => {
                const dateA = new Date(a.bill_date);
                const dateB = new Date(b.bill_date);
                return dateA.getTime() - dateB.getTime();
            },
            center: false,
            width: '120px',
            // color: 'orange'
        },
        {
            name: "Bill Type",
            selector: (row) => `${row.bill_type.charAt(0).toUpperCase()}${row.bill_type.slice(1)}`,
            sortable: false,
            center: false,
            width: '100px',
            // color: 'orange'
        },
        {
            name: "Duration",
            selector: (row) => `${formatDate(row.start_date)} - ${formatDate(row.end_date)}`,
            sortable: false,
            center: true,
            width: '200px',
            // color: 'orange'
        },
        {
            name: "Supplier",
            cell: (row) => row.supplier?.name || '',
            sortable: false,
            center: true,
        },
        {
            name: "Total Consumption",
            cell: (row) => `${row.total_consumption} ${row.utility?.si_unit || ''}`,
            sortable: false,
            center: true,
            width: '140px',
        },
        {
            name: "Total Cost",
            cell: (row) => `£${numberFormat(row.cost) || ''} ${row.currency?.major_symbol || ''}`,
            sortable: false,
            center: true,
            width: '140px',
        },
        {
            name: 'Actions',
            cell: (row) => <ActionDropdown row={row} />,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '70px',
        },
    ];

    const meterTableColumns: TableColumn<any>[] = [
        {
            name: "Property",
            cell: (row) => row.property.name,
            sortable: false,
            center: false,
        },
        {
            name: "Meter Name",
            selector: (row) => row.name,
            sortable: false,
            center: false,
        },
        {
            name: "Type",
            selector: (row) => row.meter_type,
            sortable: true,
            center: false,
            // color: 'orange'
        },
        {
            name: "Meter No.",
            selector: (row) => `${row.meter_no_1 || ''}${row.meter_no_2 || ''}`,
            sortable: true,
            center: false,
            // color: 'orange'
        },
        {
            name: "Utility",
            cell: (row) => row.utility?.name || '',
            sortable: false,
            center: false,
        },
        {
            name: "S.I. Unit",
            cell: (row) => row.utility?.si_unit || '',
            sortable: false,
            center: true,
        },
        {
            name: 'Actions',
            cell: (row) => <ActionDropdown row={row} />,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '70px',
        },
    ];

    const filteredBills = useMemo(() => {
        return bills.filter(bill => {
            const billDate = new Date(bill.bill_date);
            const billYear = billDate.getFullYear();
            return (!propertyId || bill.property?.property_id === propertyId) &&
                (!utilityId || bill.utility?.utility_id === utilityId) &&
                (!year || billYear === parseInt(year, 10));
        });
    }, [bills, propertyId, utilityId, year]);

    const formatData = async (data: YearEntry[], utility: string) => {
        // Build categories (unique months, sorted)
        const categories = data
            .flatMap((entry) => entry.label)
            .filter((value, index, self) => self.indexOf(value) === index) // unique
            .sort(
                (a, b) =>
                    new Date(`01 ${a} 2000`).getTime() -
                    new Date(`01 ${b} 2000`).getTime()
            );

        // Build series for each year
        const series = data.map((yearEntry) => {
            const dataMap = Object.fromEntries(
                yearEntry.label.map((month, i) => [month, yearEntry.data[i]])
            );
            return {
                name: yearEntry.year,
                data: categories.map((month) => dataMap[month] || 0),
            };
        });

        // Set SI unit dynamically
        const si_unit = typeMode === 'cost' ? '£' : utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'kWh' : utility === 'Water' ? 'CuM2' : '';

        return {
            categories,
            series,
            si_unit,
        };
    }

    const getData = async (e: FormEvent) => {
        e.preventDefault()

        try {
            const params: billParams = {
                site_id: selectedMeter,
                company_id: client,
                property_id: selectedProperty,
                utility_id: selectedUtility,
                year: selectedYear?.toString(),
                check_type: timeMode,
                type_mode: typeMode,
            };

            const { site_id, date, data, meter } = await getBillsData(params);
            let formattedData = {}
            switch (timeMode) {
                case 'Multiyear':
                    formattedData = {
                        name: typeMode === 'cost' ? 'Energy Cost' : (typeMode === 'status' ? 'Status' : 'Energy Consumption'),
                        categories: data.map((item: { label: number }) => item.label.toString()),
                        // series: data.map((item: { value: number; label: number }) => ({
                        //     name: item.label.toString(),
                        //     data: item.value
                        // })),
                        series: [{
                            name: typeMode === 'cost' ? 'Energy Cost' : (typeMode === 'status' ? 'Status' : 'Energy Consumption'),
                            data: data.map((item: { value: number; label: number }) => item.value)
                        }],
                        si_unit: typeMode === 'cost' ? '£' : utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'kWh' : utility === 'Water' ? 'CuM2' : '',
                    };

                    setBillData(formattedData)
                    break;
                case 'Year':
                    formattedData = {
                        name: typeMode === 'cost' ? 'Energy Cost' : (typeMode === 'status' ? 'Status' : 'Energy Consumption'),
                        categories: data.map((item: { label: string }) => item.label),
                        // series: data.map((item: { label: string; value: number; }) => ({
                        //     name: item.label,
                        //     data: item.value
                        // })),
                        series: [{
                            name: typeMode === 'cost' ? 'Energy Cost' : (typeMode === 'status' ? 'Status' : 'Energy Consumption'),
                            data: data.map((item: { value: number; label: number }) => item.value)
                        }],
                        si_unit: typeMode === 'cost' ? '£' : utility === 'Electricity' ? 'kWh' : utility === 'Gas' ? 'kWh' : utility === 'Water' ? 'CuM2' : '',
                    };

                    setBillData(formattedData)
                    break;
                case 'Year2':
                    formattedData = await formatData(data, utility);
                    setBillData(formattedData)
                    break;
                default:
                    console.log('Unknown time mode');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch data');
        }
    };

    const handleTimeModeChange = async (showPreviousYear: boolean) => {
        if (showPreviousYear == true) {
            setTimeMode('Year2');
        } else {
            setTimeMode('Year')
        }
    }

    // Prepare options for DropItem
    const propertyOptions = properties.map((property: { property_id: string; name: string }) => ({
        value: property.property_id, // Assuming property_id is the unique identifier
        label: property.name // Assuming property_name is the display name
    }));

    const utilityOptions = propertyUtilities.map((utility: { utility_id: string; name: string }) => ({
        value: utility.utility_id, // Assuming property_id is the unique identifier
        label: utility.name // Assuming property_name is the display name
    }));

    const meterOptions = propertyMeters.map((meter: { site_id: string; name: string }) => ({
        value: meter.site_id, // Assuming property_id is the unique identifier
        label: meter.name // Assuming property_name is the display name
    }));

    return (
        <>
            <Row>
                <Col md="8" className=''>
                    <Breadcrumbs pageTitle='Energy Bills' />
                </Col>
            </Row>

            <Container className='default-dashboard' fluid>
                <Row style={{ textAlign: 'center' }}>
                    <Col md={basicTab !== '2' ? 12 : 9}>
                        <Card>
                            <CardBody>
                                <CardHeader className='d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 p-0'>
                                    <Nav className='nav-primary' pills>
                                        {justifyTabNav.map((item) => (
                                            <NavItem>
                                                <NavLink href={Href} className={`${basicTab === item.id ? 'active' : ''}`} onClick={() => setBasicTab(item.id)}>{item.nav}</NavLink>
                                            </NavItem>
                                        ))}
                                    </Nav>
                                </CardHeader>
                                <CardBody className='px-0 pb-0'>
                                    <TabContent activeTab={basicTab}>
                                        <TabPane tabId='1'>
                                            <Row style={{ textAlign: "left" }}>
                                                <Col md="3">
                                                    <FormGroup>
                                                        <Label>Property</Label>
                                                        <Controller
                                                            name="property_id"
                                                            control={filterControl}
                                                            render={({ field }) => (
                                                                <Input type="select" {...field}>
                                                                    <option selected disabled value=''>Choose...</option>
                                                                    {properties.map((item: { property_id: string; name: string }, i: number) => (
                                                                        <option key={i} value={item.property_id}>{item.name}</option>
                                                                    ))}
                                                                </Input>
                                                            )}
                                                        />
                                                        {filterErrors.property_id && <p className='text-danger'>{filterErrors.property_id.message}</p>} {/* Error message */}
                                                    </FormGroup>
                                                </Col>
                                                <Col md="2">
                                                    <FormGroup>
                                                        <Label>Year</Label>
                                                        <Controller
                                                            name="year"
                                                            control={filterControl}
                                                            render={({ field }) => (
                                                                <Input type="select" {...field}>
                                                                    <option selected disabled value=''>Choose...</option>
                                                                    {[...Array(3)].map((_, i) => (
                                                                        <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>
                                                                    ))}
                                                                </Input>
                                                            )}
                                                        />
                                                        {filterErrors.year && <p className='text-danger'>{filterErrors.year.message}</p>} {/* Error message */}
                                                    </FormGroup>
                                                </Col>
                                                <Col md="2">
                                                    <FormGroup>
                                                        <Label>Utility</Label>
                                                        <Controller
                                                            name="utility_id"
                                                            control={filterControl}
                                                            render={({ field }) => (
                                                                <Input type="select" {...field}>
                                                                    <option selected disabled value=''>Choose...</option>
                                                                    {utilities.map((item: { utility_id: string; name: string }, i: number) => (
                                                                        <option key={i} value={item.utility_id}>{item.name}</option>
                                                                    ))}
                                                                </Input>
                                                            )}
                                                        />
                                                        {filterErrors.utility_id && <p className='text-danger'>{filterErrors.utility_id.message}</p>} {/* Error message */}
                                                    </FormGroup>
                                                </Col>
                                                <Col md={2}>
                                                    <button type="reset" className="btn btn-secondary mx-2 my-4" onClick={() => { resetFilter(); }}>Clear</button>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Table title='' tableColumns={billTableColumns} data={filteredBills} filterKeys={filterKeys} />
                                            </Row>
                                        </TabPane>
                                        {/* <TabPane tabId='2'>
                                            <Table title='' tableColumns={meterTableColumns} data={meters} />
                                        </TabPane> */}
                                        <TabPane tabId='2'>
                                            <Row>
                                                <Col md="12">
                                                    <CardBody>
                                                        {billData && activeChart == 'bar' && <ColumnChart title='Chart' data={billData} height={400} width={500} size={12} />}
                                                        {billData && activeChart == 'line' && <BasicAreaChart title='Chart' data={billData} height={400} width={500} size={12} />}
                                                        {/* ) : (
                                                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                                                <p>No chart data available. Please select filters and click "Get Data".</p>
                                                            </div>
                                                        )} */}
                                                    </CardBody>
                                                </Col>
                                            </Row>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </CardBody>
                        </Card>
                    </Col>
                    {basicTab == '2' &&
                        <Col md="3">
                            <Card>
                                <CardBody>
                                    <Form className="theme-form" onSubmit={getData}>
                                        <H4 className='text-center'>Filter</H4>
                                        <Row className='text-start'>
                                            <Col md='12'>
                                                <Row className='text-center align-center pl-2'>
                                                    <Col className={`border ${activeChart === 'bar' ? 'bg-primary' : ''} cursor-pointer`} onClick={() => setActiveChart('bar')}>
                                                        <ChartColumnIncreasing />
                                                    </Col>
                                                    <Col className={`border ${activeChart === 'line' ? 'bg-primary' : ''} cursor-pointer`} onClick={() => setActiveChart('line')}>
                                                        <ChartLine />
                                                    </Col>
                                                </Row>
                                            </Col>

                                            <Col className='mt-2' md='12'>
                                                <FormGroup>
                                                    <SelectDropdown title={'Properties'} placeholder='Property' options={propertyOptions} value={selectedProperty} onOptionSelected={(val) => handlePropertyChange(val)} />
                                                    <div className="alert alert-danger" id="company_mess_div" hidden>
                                                        <p id="company_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col className='mt-2' md='12'>
                                                <FormGroup>
                                                    <SelectDropdown title={'Utilities'} placeholder='Utility' options={utilityOptions} value={selectedUtility} onOptionSelected={(val) => { handleUtilityChange(val); }} onOptionTextSelected={(val) => { setUtility(String(val)) }} />
                                                    <div className="alert alert-danger" id="company_mess_div" hidden>
                                                        <p id="company_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>

                                            <Col className='mt-2' md='12'>
                                                <FormGroup>
                                                    <SelectDropdown title={'Meters'} placeholder='Meter' options={[{ value: 'all', label: 'All' }, ...meterOptions]} value={selectedMeter} onOptionSelected={(val) => setSelectedMeter(val as string)} />
                                                    <div className="alert alert-danger" id="meter_mess_div" hidden>
                                                        <p id="meter_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>

                                            <Col md='12'>
                                                <FormGroup>
                                                    <Label>Data Type</Label>
                                                    <Row className='text-center align-center pl-2'>
                                                        <Col className={`border ${typeMode === 'consumption' ? 'bg-primary' : ''} cursor-pointer`} onClick={() => setTypeMode('consumption')}>
                                                            Consumption
                                                        </Col>
                                                        <Col className={`border ${typeMode === 'cost' ? 'bg-primary' : ''} cursor-pointer`} onClick={() => setTypeMode('cost')}>
                                                            Cost
                                                        </Col>
                                                    </Row>
                                                </FormGroup>
                                            </Col>
                                            <Col md='12'>
                                                <FormGroup>
                                                    <Label>Mode Selection</Label>
                                                    <Row className='text-center'>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Multiyear" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Multiyear")}>Multiyear</Col>
                                                        <Col md="6" className={`border py-1 ${(timeMode === 'Year' || timeMode === 'Year2') ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Year")}>Year</Col>
                                                    </Row>
                                                    {/* <Row className='text-center'>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Monthly" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Monthly")}>Monthly</Col>
                                                        <Col md="6" className={`border py-1 ${timeMode === "Weekly" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Weekly")}>Weekly</Col>
                                                        <Col md="6" className={`border py-1 ${timeMode === "From/To" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("From/To")}>From/To</Col>
                                                    </Row> */}
                                                    {/* <Row className='text-center'>
                                                                                <Col md="6" className={`border py-1 ${timeMode === "Daily" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("Daily")}>Daily</Col>
                                                                                <Col md="6" className={`border py-1 ${timeMode === "From/To" ? "bg-primary" : ""} cursor-pointer`} onClick={() => setTimeMode("From/To")}>From/To</Col>
                                                                            </Row> */}
                                                    <div className="alert alert-danger" id="mode_mess_div" hidden>
                                                        <p id="mode_message"></p>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            {(timeMode === 'Year' || timeMode === 'Year2') && <>
                                                <Col md='12'>
                                                    <FormGroup>
                                                        <Row className='justify-content-center text-center'>
                                                            {Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => 2020 + i).map((year) => (
                                                                <Col md="3" className={`border py-1 m-1 ${selectedYear === year ? "bg-primary" : ""} cursor-pointer`} style={{ fontSize: '0.6rem' }} onClick={() => { setSelectedYear(year); }}>{year}</Col>
                                                            ))}
                                                        </Row>
                                                    </FormGroup>
                                                </Col>

                                                <Col md='12'>
                                                    <FormGroup check>
                                                        <Label check>
                                                            <Input type="checkbox" onChange={(e) => handleTimeModeChange(e.target.checked)} />{' '}
                                                            With Previous Year
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            </>
                                            }
                                        </Row>
                                        <Row className='justify-content-center'>
                                            <Col md="12">
                                                <Btn className='w-full' color="primary" type="submit">Get Data</Btn>
                                            </Col>
                                        </Row>
                                    </Form>

                                </CardBody>
                            </Card>
                        </Col>
                    }
                </Row>
            </Container>
        </>
    );
}